import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing temporary form data storage
 * Automatically saves form data to sessionStorage (temporary - cleared on page close)
 * Data is cleared when the form is successfully submitted or when explicitly cleared
 */
export const useFormStorage = (storageKey, initialData = {}) => {
  const [formData, setFormData] = useState(initialData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load data from sessionStorage on mount (only if it exists from current session)
  useEffect(() => {
    try {
      const savedData = sessionStorage.getItem(storageKey);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      console.warn('Failed to load form data from sessionStorage:', error);
    }
  }, [storageKey]);

  // Save data to sessionStorage whenever formData changes
  useEffect(() => {
    if (hasUnsavedChanges && Object.keys(formData).length > 0) {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(formData));
      } catch (error) {
        console.warn('Failed to save form data to sessionStorage:', error);
      }
    }
  }, [formData, storageKey, hasUnsavedChanges]);

  // Update form data
  const updateFormData = useCallback((newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
    setHasUnsavedChanges(true);
  }, []);

  // Clear all stored data
  const clearStoredData = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey);
      setFormData(initialData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.warn('Failed to clear form data from sessionStorage:', error);
    }
  }, [storageKey, initialData]);

  // Reset form data to initial state without clearing localStorage
  const resetFormData = useCallback(() => {
    setFormData(initialData);
    setHasUnsavedChanges(false);
  }, [initialData]);

  // Check if there's any saved data
  const hasStoredData = useCallback(() => {
    try {
      const savedData = sessionStorage.getItem(storageKey);
      return savedData && Object.keys(JSON.parse(savedData)).length > 0;
    } catch (error) {
      return false;
    }
  }, [storageKey]);

  return {
    formData,
    updateFormData,
    clearStoredData,
    resetFormData,
    hasUnsavedChanges,
    hasStoredData: hasStoredData()
  };
};

export default useFormStorage;
